import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import {
  getPaginationInfoItems,
  IBank,
  ICreatePayoutAccountPayload,
  IPaginatedResponse,
  IPayoutAccount,
  IResolveBankAccountPayload,
  IResolveResult,
  IUpdatePayoutAccountLabelPayload
} from '@pharvio/core/shared';
import { PayoutAccountsDataService } from './repositories/payout-accounts-data.service';
import { BANKS_FIXTURES, PAYOUT_ACCOUNTS_FIXTURES } from './fixtures';

@Injectable()
export class InMemoryPayoutAccountsDataService extends PayoutAccountsDataService {
  private accounts: IPayoutAccount[] = [...PAYOUT_ACCOUNTS_FIXTURES];

  getAccounts(): Observable<IPaginatedResponse<IPayoutAccount>> {
    const { items: data, ...meta } = getPaginationInfoItems({
      dataList: this.accounts,
      currentPage: 1,
      pageSize: this.accounts.length
    });
    return of({
      data,
      meta
    }).pipe(delay(150));
  }

  createAccount(
    payload: ICreatePayoutAccountPayload
  ): Observable<IPayoutAccount> {
    const bank = BANKS_FIXTURES.find((b) => b.code === payload.bankCode);
    const account: IPayoutAccount = {
      id: crypto.randomUUID(),
      branchId: payload.branchId,
      label: payload.label,
      bankCode: payload.bankCode,
      bankName: bank?.name ?? payload.bankCode,
      // accountNumberMasked: `••••${payload.accountNumber.slice(-4)}`,
      accountName: 'MOCK ACCOUNT NAME',
      accountNumber: payload.accountNumber,
      status: 'ACTIVE',
      suspendedReason: null,
      firstUsedAt: null,
      createdAt: new Date().toISOString()
    };
    // Replace existing active account for same scope
    this.accounts = this.accounts.map((a) =>
      a.branchId === payload.branchId && a.status === 'ACTIVE'
        ? { ...a, status: 'ARCHIVED' as const }
        : a
    );
    this.accounts = [...this.accounts, account];
    return of(account).pipe(delay(150));
  }

  archiveAccount(id: string): Observable<void> {
    this.accounts = this.accounts.map((a) =>
      a.id === id ? { ...a, status: 'ARCHIVED' as const } : a
    );
    return of(undefined).pipe(delay(150));
  }

  updateLabel(
    id: string,
    payload: IUpdatePayoutAccountLabelPayload
  ): Observable<IPayoutAccount> {
    this.accounts = this.accounts.map((a) =>
      a.id === id ? { ...a, label: payload.label } : a
    );
    const updated = this.accounts.find((a) => a.id === id)!;
    return of(updated).pipe(delay(150));
  }

  getBanks(): Observable<IBank[]> {
    return of([...BANKS_FIXTURES]).pipe(delay(100));
  }

  resolveBankAccount(
    payload: IResolveBankAccountPayload
  ): Observable<IResolveResult> {
    const bank = BANKS_FIXTURES.find((b) => b.code === payload.bankCode);
    return of({
      accountName: 'MOCK PHARMACY LTD',
      accountNumber: payload.accountNumber,
      bankCode: payload.bankCode,
      bankName: bank?.name ?? payload.bankCode
    }).pipe(delay(600));
  }
}
